import React, { useEffect, useRef, useState } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { ExitSurveyOptionType } from "lib/types/checkout";
import { GenericDialog } from "components/common/dialogs/GenericDialog";
import { X, SealPercent } from "@phosphor-icons/react";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { getExitCouponNudgeConfig } from "lib/utils/checkout";
import { currencyFormatter } from "lib/utils/formatters";
import { Icon } from "@shopflo/ui/icon";
import { postSurvey } from "lib/core/apiClient";
import DotsLoader from "../loaders/DotsLoader";

interface SurveyPopupProps {
  isOpen: boolean;
  closePopup: () => void;
  closeCheckout: () => void;
  onApplyAndStay?: () => void;
  isLoading?: boolean;
  savingsAmount?: number;
}

const SurveyPopup: React.FC<SurveyPopupProps> = ({
  isOpen,
  closePopup,
  closeCheckout,
  onApplyAndStay,
  isLoading = false,
  savingsAmount,
}) => {
  const { t } = useLocale();
  const {
    state: { checkoutId, exitSurveyOptions, billing, exitCouponNudgeRuntime },
  } = useCheckoutContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();

  const config = getExitCouponNudgeConfig(
    checkoutMetadata?.uiAttributes?.exitCouponNudgeConfig,
    exitCouponNudgeRuntime,
  );

  const [selected, setSelected] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [showError, setShowError] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const renderedOptions = [...exitSurveyOptions].sort((a, b) =>
    a.optionId === "others" ? 1 : b.optionId === "others" ? -1 : 0,
  );
  const othersIndex = renderedOptions.length - 1;

  useEffect(() => {
    if (!isOpen) return;
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_SURVEY_FORM_LOADED,
      eventType: "load",
    });
  }, [isOpen, sendAnalyticsEvent]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (selected === othersIndex) textareaRef.current?.focus();
  }, [selected, othersIndex]);

  const handleSubmitFeedback = async () => {
    if (selected === null) {
      setShowError(true);
      return;
    }
    const payload = {
      rank: selected + 1,
      drop_off_reason: renderedOptions[selected].optionId,
      drop_off_answer:
        selected === renderedOptions.length - 1 ? reason : renderedOptions[selected].optionLabel,
    };

    try {
      await postSurvey(payload, checkoutId);
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_SURVEY_FORM_ANSWERED,
        eventType: "click",
      });
    } catch (e) {
      console.error(e);
    } finally {
      closePopup();
      closeTimeoutRef.current = setTimeout(() => closeCheckout(), 600);
    }
  };

  return (
    <>
      <GenericDialog
        isOpen={isOpen}
        setIsOpen={closePopup}
        translateAxis="y"
        dialogOverlay
        modalType="EXIT_SURVEY"
        customClass="!overflow-hidden md:!top-auto md:absolute rounded-t-2xl !p-0 max-h-[85vh]">
        <div className="flex h-full min-h-0 max-h-[85vh] flex-col antialiased">
          {/* Exit coupon hero — only when coming from exit coupon nudge */}
          {config && onApplyAndStay && (
            <div className="relative shrink-0 overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute z-0 left-[15%] top-[64%] -translate-x-1/2 -translate-y-1/2 scale-150">
                <div
                  className="h-[620px] w-[700px] will-change-transform animate-exit-coupon-spin"
                  style={{
                    background: `conic-gradient(from 195deg at 50% 50%, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 0deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 47deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 96deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 121deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 142deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 161deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 181deg, color-mix(in srgb, ${config.badge.bgColor} 4%, transparent) 198deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 223deg, transparent 245deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 260deg, rgba(255,255,255,0) 282deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 310deg, rgba(255,255,255,0) 334deg, color-mix(in srgb, ${config.badge.bgColor} 30%, transparent) 360deg)`,
                  }}
                />
              </div>
              <div
                className="relative z-10 flex items-end justify-between px-6 py-4"
                style={{
                  boxShadow: "inset 0px -1px 0px 0px hsl(0deg 0% 2% / 10%)",
                  background:
                    "radial-gradient(ellipse 230% 160% at 60% 90%, rgba(255,255,255,0) 20%, rgba(255,255,255,1) 65%)",
                }}>
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-xs-semibold" style={{ color: config.nudge.textColor }}>
                    {t("offer_still_there")}
                  </span>
                  {config.concessionAmount > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-[26px] font-bold leading-[33px] tracking-tighter text-zinc-900">
                        {config.amountType === "PERCENTAGE"
                          ? `${config.concessionAmount}%`
                          : `₹${config.concessionAmount}`}
                      </span>
                      <span className="text-[21px] font-semibold leading-[28px] tracking-tighter text-zinc-900/50">
                        {t("off")}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <Icon
                    IconComponent={X}
                    size="sm"
                    className="text-zinc-500 cursor-pointer"
                    onClick={closePopup}
                    iconProps={{ weight: "bold" }}
                  />
                  <button
                    type="button"
                    className="cursor-pointer text-text-md-semibold flex flex-col items-center justify-center rounded-xl px-4 text-btnPrimaryText touch-manipulation bg-primary-dark active:[background:color-mix(in_srgb,var(--flo-primary-dark-color)_80%,#71717A_20%)]"
                    style={{ height: 44, gap: 4 }}
                    onClick={onApplyAndStay}>
                    {isLoading ? <DotsLoader color="var(--flo-primary-btn-text-color)" /> : t("apply_offer")}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 pb-6 pt-5">
            {/* Scrollable: header, savings, options, reason */}
            <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto">
              {/* Header — hidden in combined exit coupon + survey mode per Figma design */}
              {!(config && onApplyAndStay) && (
                <div className="flex items-center justify-between pb-2.5">
                  <h1 className="text-text-md-semibold text-zinc-900">{t("why_leaving")}</h1>
                  <div className="flex h-5 items-center justify-center flex-shrink-0 cursor-pointer">
                    <Icon
                      IconComponent={X}
                      size="sm"
                      className="text-zinc-500"
                      onClick={closePopup}
                      iconProps={{ weight: "bold" }}
                    />
                  </div>
                </div>
              )}
              {/* Savings nudge — standalone survey with an already-applied discount */}
              {!config && savingsAmount && savingsAmount > 0 && (
                <p className="flex items-center self-start text-text-sm-semibold text-yellow-dark">
                  <SealPercent className="h-4 w-4 flex-shrink-0" weight="fill" />
                  <span className="ml-1">{t("about_to_lose_savings")}</span>
                  <span className="ml-0.5">{currencyFormatter(savingsAmount)}</span>
                  <span className="ml-0.5">{t("in_savings")}</span>
                </p>
              )}
              {/* Savings nudge — only shown when there's a known exit coupon concession or an already-applied exit coupon */}
              {config?.concessionAmount != null &&
                config.concessionAmount > 0 &&
                (() => {
                  // When the exit coupon is already applied, billing.discount already includes its value — don't add concessionAmount again
                  const unappliedConcession = !config.alreadyApplied ? config.concessionAmount : 0;
                  const isFlat = config.amountType === "FLAT";
                  // For flat: sum existing cart savings + unapplied exit coupon concession
                  // For percentage: show existing flat savings (if any) + percentage label separately
                  const existingDiscount = billing?.discount ?? 0;
                  const flatTotal = isFlat ? existingDiscount + unappliedConcession : existingDiscount;
                  const showFlat = flatTotal > 0;
                  const showPercent = !isFlat && unappliedConcession > 0;
                  if (!showFlat && !showPercent) return null;
                  return (
                    <p className="flex items-center self-start text-text-sm-semibold text-yellow-dark">
                      <SealPercent className="h-4 w-4 flex-shrink-0" weight="fill" />
                      <span className="ml-1">{t("about_to_lose_savings")}</span>
                      {showFlat && <span className="ml-0.5">{currencyFormatter(flatTotal)}</span>}
                      {showFlat && showPercent && <span className="ml-0.5">+</span>}
                      {showPercent && <span className="ml-0.5">{`${config.concessionAmount}%`}</span>}
                      <span className="ml-0.5">{t("in_savings")}</span>
                    </p>
                  );
                })()}

              {/* Radio options */}
              {(exitSurveyOptions?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-1 mb-1">
                  {renderedOptions.map((option: ExitSurveyOptionType, i: number) => {
                    const isSelected = selected === i;
                    return (
                      <label
                        key={option.optionId}
                        className="flex cursor-pointer items-center gap-2"
                        onClick={() => {
                          setSelected(i);
                          setShowError(false);
                        }}>
                        <span
                          className="flex flex-shrink-0 items-center justify-center rounded-full transition-colors"
                          style={{
                            width: 16,
                            height: 16,
                            border: isSelected ? "5px solid #1a1a1a" : "1.5px solid #D1D5DC",
                            background: "#FFFFFF",
                          }}
                        />
                        <span className="text-text-sm-medium text-zinc-800 opacity-70 py-1">
                          {option.optionLabel}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Reason textarea */}
              <div className="flex flex-col gap-1.5">
                <label className="text-text-xs-medium text-[#71717B] overflow-hidden text-ellipsis px-1.5">
                  {t("reason_optional")}
                </label>
                <textarea
                  ref={textareaRef}
                  className="text-text-sm-regular w-full resize-none rounded-xl border border-zinc-200 px-3 py-2.5 text-zinc-700 placeholder:text-zinc-400 placeholder:text-text-sm-regular outline-none focus:border-zinc-300"
                  rows={3}
                  placeholder={t("reason_placeholder")}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                  disabled={selected !== othersIndex}
                />
              </div>
            </div>

            {/* Actions — always visible */}
            <div className="flex shrink-0 flex-col gap-3 pt-2.5">
              {showError && <p className="text-text-xs-regular text-red-500">{t("please_select_reason")}</p>}
              <button
                type="button"
                className={`w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation ${
                  onApplyAndStay
                    ? "border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10"
                    : "text-btnPrimaryText bg-primary-dark active:[background:color-mix(in_srgb,var(--flo-primary-dark-color)_80%,#71717A_20%)]"
                }`}
                onClick={handleSubmitFeedback}>
                {t("submit_feedback")}
              </button>
              <button
                type="button"
                className="w-full cursor-pointer py-3 rounded-[10px] text-text-md-semibold touch-manipulation border border-zinc-900/20 text-zinc-900/70 active:bg-zinc-500/10"
                onClick={() => {
                  closePopup();
                  closeCheckout();
                }}>
                {t("skip_and_exit")}
              </button>
            </div>
          </div>
        </div>
      </GenericDialog>

      {isLoading && <OverlaySpinner />}
    </>
  );
};

export default SurveyPopup;
